import debounce from 'debounce-promise';
import { useField } from 'formik';
import React from 'react';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

import closeIcon from 'images/directory/x_icon.svg';

import { capitalize } from '../../utils/formatting';
import {
  AutocompleteOption,
  AutocompleteRecord,
  CreateRecordResponse,
} from '../../utils/interfaces';
import { Pill } from '../BaseUI';

const DEBOUNCE_MS = 500;

type Option = AutocompleteOption | AutocompleteRecord;
interface MultiValueAutocompleteFieldProps {
  className?: string;
  createMethod?: (value: string) => Promise<CreateRecordResponse>;
  fetchMethod?: (value: string) => Promise<Option[]>;
  isClearable?: boolean;
  isCreatable?: boolean;
  isDisabled?: boolean;
  label?: string;
  name: string;
  options?: Option[];
  placeholder?: string;
  showPills?: boolean;
}

export default function MultiValueAutocompleteField(props: MultiValueAutocompleteFieldProps) {
  const {
    label,
    name,
    options,
    fetchMethod,
    createMethod,
    isCreatable = true,
    showPills = true,
  } = props;
  const [field, meta, helpers] = useField(props);

  const SelectFieldComponent = isCreatable ? AsyncCreatableSelect : AsyncSelect;

  const asyncLoadOptions = async (value) => {
    let loadedOptions = [];
    if (value.length === 0) {
      loadedOptions = options || [];
    } else {
      // if fetchMethod is present, use it, otherwise, just look at the options list
      loadedOptions = fetchMethod
        ? await fetchMethod(value)
        : options.filter((o) => o.label.toLowerCase().includes(value.toLowerCase()));
    }
    return loadedOptions.map((option) => ({
      label: option?.label || option,
      value: option?.value || option,
    }));
  };

  const debounceLoadOptions = debounce((value) => asyncLoadOptions(value), DEBOUNCE_MS, {
    leading: true,
  });

  const handleChange = (value) => {
    helpers.setValue(
      value || {
        label: '',
        value: '',
      }
    );
  };

  const onCreateOption = async (value) => {
    const result = await createMethod(value);
    handleChange([...field.value, { label: value, value: result.id }]);
  };

  const handleRemoveValue = (option) => {
    if (props.isDisabled) return;
    const list = field.value.filter((val) => val.value !== option.value);
    handleChange(list);
  };

  return (
    <div
      className={`MultiValueAutocompleteField form-group ${
        meta.touched && meta.error ? 'is-invalid' : ''
      }`}
    >
      {label && (
        <label className="input-label" htmlFor={name}>
          {label}
        </label>
      )}
      <div className="forum-group-content">
        <div className="input-container">
          <SelectFieldComponent
            className={meta.touched && meta.error ? 'is-invalid' : ''}
            classNamePrefix="react-select"
            onChange={(value) => helpers.setValue(value)}
            onBlur={() => helpers.setTouched(true)}
            loadOptions={debounceLoadOptions}
            isClearable={false}
            isMulti={true}
            value={field.value}
            inputId={name}
            controlShouldRenderValue={!showPills}
            backspaceRemovesValue={false}
            onCreateOption={createMethod && onCreateOption}
            {...props}
          />
          {/* meta.error is of type string, but in this case the error lives in an object of type AutocompleteOption, the following code does a force cast */}
          {meta.touched && meta.error && (
            <div className="invalid-feedback">
              {(meta.error as unknown as AutocompleteOption).value}
            </div>
          )}
        </div>
        {showPills && (
          <div className="pill-container">
            {field.value &&
              field.value.map((val, id) => (
                <Pill
                  onClick={() => handleRemoveValue(val)}
                  className="--neutral"
                  /* capitalize first letter of each word (can't use lodash's startCase because it removes special characters) */
                  text={capitalize(val.label)}
                  icon={closeIcon}
                  key={id}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
}
